// Settings — left sub-nav shell + sections (General, Company, Integrations, Team, Billing, …)
import { Fragment, useState } from "react";
import posthog from "@/lib/posthog";
import { Icon } from "@/components/Icon";
import { SectionHead, Segmented, Avatar } from "@/components/primitives";
import { useNotImplemented } from "@/hooks/use-not-implemented";
import { DATA } from "@/data";

/* ── Team ───────────────────────────────────────────────────────── */
const RolePill = ({ role }: { role: string }) => role === "Admin"
  ? <span style={{ background: "#111827", color: "#fff", fontSize: 12, fontWeight: 600, padding: "4px 12px", borderRadius: 8 }}>Admin</span>
  : <span style={{ background: "var(--surface-3)", color: "var(--ink-2)", fontSize: 12, fontWeight: 600, padding: "4px 12px", borderRadius: 8 }}>Member</span>;

const TeamSection = () => {
  const notify = useNotImplemented();
  return (
    <div className="fade-up">
      <SectionHead title="Team" sub="People with access to Northwind Holdings." size="lg"
        actions={<button className="btn btn-pri" onClick={() => { posthog.capture("settings_invite_member_clicked"); notify("Invite member"); }}><Icon name="users" size={15} stroke="#fff" /> Invite member</button>} />
      <div className="eyebrow" style={{ marginBottom: 10 }}>Members · {DATA.teamMembers.length}</div>
      <div className="card" style={{ marginBottom: 26 }}>
        {DATA.teamMembers.map((m, i) => (
          <div key={m.email} style={{ display: "flex", alignItems: "center", gap: 13, padding: "14px 18px", borderBottom: i < DATA.teamMembers.length - 1 ? "1px solid var(--line-2)" : "none" }}>
            <Avatar name={m.init} size={38} color={m.color} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 7 }}>
                {m.name}{(m as { you?: boolean }).you && <span style={{ fontSize: 12, fontWeight: 500, color: "var(--ink-4)" }}>(you)</span>}
              </div>
              <div style={{ fontSize: 12.5, color: "var(--ink-4)", marginTop: 1 }}>{m.email}</div>
            </div>
            <RolePill role={m.role} />
            <button className="btn btn-ghost btn-sm btn-icon" onClick={() => { posthog.capture("settings_member_menu_clicked", { member: m.name }); notify("Member options"); }}><Icon name="more" size={16} stroke="var(--ink-4)" /></button>
          </div>
        ))}
      </div>
      <div className="eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 7 }}>
        <Icon name="chevdown" size={13} stroke="var(--ink-4)" /> Pending invites · {DATA.teamPending.length}
      </div>
      <div className="card">
        {DATA.teamPending.map((p, i) => (
          <div key={p.email} style={{ display: "flex", alignItems: "center", gap: 13, padding: "14px 18px", borderBottom: i < DATA.teamPending.length - 1 ? "1px solid var(--line-2)" : "none" }}>
            <span style={{ width: 38, height: 38, borderRadius: 50, background: "var(--surface-3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name="users" size={17} stroke="var(--ink-4)" />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{p.email}</div>
              <div style={{ fontSize: 12.5, color: "var(--warn)", marginTop: 1 }}>Invite pending</div>
            </div>
            <RolePill role={p.role} />
            <button className="btn btn-sec btn-sm" onClick={() => { posthog.capture("settings_invite_resent", { email: p.email }); notify("Resend invite"); }}>Resend</button>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── General & Company ──────────────────────────────────────────── */
const FieldRow = ({ label, value, hint }: { label: string; value: string; hint?: string }) => {
  const notify = useNotImplemented();
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "15px 18px", borderBottom: "1px solid var(--line-2)" }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13.5, fontWeight: 500 }}>{label}</div>
        {hint && <div style={{ fontSize: 11.5, color: "var(--ink-4)", marginTop: 2 }}>{hint}</div>}
      </div>
      <span style={{ fontSize: 13.5, color: "var(--ink-2)", fontWeight: 500, marginRight: 12 }}>{value}</span>
      <button className="btn btn-ghost btn-sm" onClick={() => { posthog.capture("settings_field_edit_clicked", { field: label }); notify(`Edit ${label}`); }}>Edit</button>
    </div>
  );
};

const GeneralSection = () => {
  const notify = useNotImplemented();
  return (
    <div className="fade-up">
      <SectionHead title="General" sub="Workspace-wide preferences." size="lg" />
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-h"><div className="card-t">Workspace</div></div>
        <FieldRow label="Workspace name" value="Northwind Holdings" />
        <FieldRow label="Base currency" value="USD ($)" />
        <FieldRow label="Time zone" value="America / New York" hint="Used for reports and agent schedules" />
        <div style={{ display: "flex", alignItems: "center", padding: "15px 18px" }}>
          <div style={{ flex: 1, fontSize: 13.5, fontWeight: 500 }}>Week starts on</div>
          <Segmented value="mon" onChange={(v) => { posthog.capture("settings_preference_changed", { setting: "week_start", value: v }); notify("Week starts on"); }} options={[{ value: "sun", label: "Sun" }, { value: "mon", label: "Mon" }]} />
        </div>
      </div>
      <div className="card">
        <div className="card-h"><div className="card-t">Appearance</div></div>
        <div style={{ display: "flex", alignItems: "center", padding: "15px 18px" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 500 }}>Theme</div>
            <div style={{ fontSize: 11.5, color: "var(--ink-4)", marginTop: 2 }}>Light is recommended for finance work</div>
          </div>
          <Segmented value="light" onChange={(v) => { posthog.capture("settings_preference_changed", { setting: "theme", value: v }); notify("Theme"); }} options={[{ value: "light", label: "Light" }, { value: "auto", label: "Auto" }, { value: "dark", label: "Dark" }]} />
        </div>
      </div>
    </div>
  );
};

const CompanySection = () => (
  <div className="fade-up">
    <SectionHead title="Company" sub="Legal and fiscal details used across reports and filings." size="lg" />
    <div className="card">
      <div className="card-h"><div className="card-t">Company profile</div></div>
      <FieldRow label="Legal name" value="Northwind Holdings, Inc." />
      <FieldRow label="EIN" value="87-402••••" />
      <FieldRow label="Fiscal year end" value="December 31" />
      <FieldRow label="Entity type" value="C-Corporation" />
      <FieldRow label="Registered address" value="Brooklyn, NY" hint="Primary place of business" />
    </div>
  </div>
);

/* ── Billing ────────────────────────────────────────────────────── */
const BillingSection = () => {
  const notify = useNotImplemented();
  const [plan, setPlan] = useState("partner");
  const PLANS = DATA.billingPlans;
  const ROWS = DATA.billingRows;
  const cur = PLANS.find((p) => p.id === plan)!;
  const cell = (v: string | boolean) => v === true
    ? <Icon name="check" size={16} stroke="var(--pos)" sw={2.4} />
    : v === false
      ? <Icon name="x" size={15} stroke="var(--ink-5)" sw={2.2} />
      : <span style={{ fontSize: 13, color: "var(--ink-2)" }}>{v}</span>;
  return (
    <div className="fade-up">
      <SectionHead title="Billing" sub="Your plan, usage and invoices." size="lg"
        actions={<button className="btn btn-sec" onClick={() => { posthog.capture("billing_invoices_clicked"); notify("Invoices"); }}><Icon name="download" size={15} stroke="var(--ink-3)" /> Invoices</button>} />
      {/* current plan banner */}
      <div className="card ai-surface" style={{ padding: "16px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 14 }}>
        <span style={{ width: 42, height: 42, borderRadius: 11, background: "var(--brand)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="spark" size={21} stroke="#fff" />
        </span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>{cur.name} plan <span className="badge b-brand">Current</span></div>
          <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 2 }}>{cur.price}{cur.per} · renews Jun 1, 2026 · billed annually</div>
        </div>
        <button className="btn btn-sec" onClick={() => { posthog.capture("billing_manage_payment_clicked"); notify("Manage payment"); }}>Manage payment</button>
      </div>
      {/* pricing table */}
      <div className="card" style={{ overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr repeat(3,1fr)" }}>
          <div style={{ padding: "16px 18px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--ink-4)" }}>Feature</div>
          {PLANS.map((p) => {
            const on = p.id === plan;
            return (
              <div key={p.id} onClick={() => { posthog.capture("billing_plan_selected", { plan: p.id, via: "header" }); setPlan(p.id); }} style={{
                padding: "14px 18px", borderBottom: "1px solid var(--line)", cursor: "pointer", textAlign: "center",
                background: on ? "var(--brand)" : "var(--surface-2)", color: on ? "#fff" : "var(--ink)", position: "relative", transition: "background .15s",
              }}>
                <div style={{ fontSize: 14.5, fontWeight: 700 }}>{p.name}</div>
                <div style={{ fontSize: 12, marginTop: 2, color: on ? "rgba(255,255,255,.85)" : "var(--ink-4)" }}>{p.price}<span style={{ fontSize: 11 }}>{p.per}</span></div>
                {on && <span style={{ position: "absolute", top: 8, right: 10, fontSize: 10, fontWeight: 700, background: "rgba(255,255,255,.22)", padding: "2px 7px", borderRadius: 6 }}>SELECTED</span>}
              </div>
            );
          })}
          {ROWS.map((r, ri) => (
            <Fragment key={ri}>
              <div style={{ padding: "13px 18px", borderBottom: ri < ROWS.length - 1 ? "1px solid var(--line-2)" : "none", fontSize: 13, fontWeight: 500, color: "var(--ink-2)", background: ri % 2 ? "var(--surface-2)" : "#fff" }}>{r.f}</div>
              {r.v.map((v, ci) => {
                const on = PLANS[ci].id === plan;
                return (
                  <div key={ci} style={{
                    padding: "13px 18px", borderBottom: ri < ROWS.length - 1 ? "1px solid var(--line-2)" : "none",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: on ? "var(--brand-tint)" : ri % 2 ? "var(--surface-2)" : "#fff",
                    boxShadow: on ? "inset 1px 0 0 var(--brand-line), inset -1px 0 0 var(--brand-line)" : "none",
                  }}>{cell(v as string | boolean)}</div>
                );
              })}
            </Fragment>
          ))}
          <div style={{ background: "#fff" }}></div>
          {PLANS.map((p) => {
            const on = p.id === plan;
            return (
              <div key={p.id} style={{
                padding: "14px 16px", display: "flex", justifyContent: "center",
                background: on ? "var(--brand-tint)" : "#fff",
                boxShadow: on ? "inset 1px 0 0 var(--brand-line), inset -1px 0 0 var(--brand-line), inset 0 -1px 0 var(--brand-line)" : "none",
              }}>
                <button className={"btn btn-sm " + (on ? "btn-pri" : "btn-sec")} style={{ width: "100%" }} onClick={() => { posthog.capture("billing_plan_selected", { plan: p.id, via: "choose_button" }); setPlan(p.id); }}>
                  {on ? "Current plan" : p.id === "free" ? "Downgrade" : "Choose " + p.name}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const SimpleSection = ({ title, sub }: { title: string; sub: string }) => (
  <div className="fade-up">
    <SectionHead title={title} sub={sub} size="lg" />
    <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--ink-4)" }}>
      <Icon name="sliders" size={26} stroke="var(--ink-5)" />
      <div style={{ fontSize: 13.5, marginTop: 10 }}>{title} settings live here.</div>
    </div>
  </div>
);

/* ═══ INTEGRATIONS ══════════════════════════════════════════════════ */
// Brand logo tile — simple monogram/glyph on a branded surface
const LogoTile = ({ label, bg, fg = "#fff", glyph, size = 48, radius = 13, soon }: {
  label: string; bg: string; fg?: string; glyph?: string; size?: number; radius?: number; soon?: boolean;
}) => (
  <span style={{
    width: size, height: size, borderRadius: radius, background: bg, color: fg,
    display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 700,
    fontSize: size * 0.42, flexShrink: 0, boxShadow: "inset 0 0 0 1px rgba(23, 49, 58,.04)",
    filter: soon ? "grayscale(.15)" : "none",
  }}>{glyph || label[0]}</span>
);

interface IntegrationItem {
  name: string; note?: string; bg: string; fg?: string; glyph?: string; sub?: string;
}
interface ConnectedItem extends IntegrationItem {
  lastSync: string;
}

export const IntegrationsPage = () => {
  const notify = useNotImplemented();
  const [connected, setConnected] = useState<ConnectedItem[]>(DATA.integrationsConnected as ConnectedItem[]);
  const [syncingName, setSyncingName] = useState<string | null>(null);
  const [connecting, setConnecting] = useState<IntegrationItem | null>(null); // item being connected (modal)
  const [phase, setPhase] = useState<"consent" | "authorizing" | "done">("consent");
  const [flash, setFlash] = useState<string | null>(null); // name just connected (row highlight)

  const isConnected = (name: string) => connected.some((c) => c.name === name);

  const doSync = (name: string) => {
    posthog.capture("integration_sync_clicked", { integration: name });
    setSyncingName(name);
    setTimeout(() => {
      setConnected((cs) => cs.map((c) => (c.name === name ? { ...c, lastSync: "just now" } : c)));
      setSyncingName(null);
    }, 1400);
  };

  const openConnect = (it: IntegrationItem) => {
    posthog.capture("integration_connect_started", { integration: it.name });
    setConnecting(it); setPhase("consent");
  };
  const confirmConnect = () => {
    posthog.capture("integration_connect_authorized", { integration: connecting?.name });
    setPhase("authorizing");
    setTimeout(() => {
      const it = connecting!;
      setConnected((cs) => [...cs, { ...it, sub: it.sub || "Connected · " + new Date().toLocaleDateString(), lastSync: "just now" }]);
      setPhase("done");
      setTimeout(() => {
        setConnecting(null);
        setFlash(it.name);
        setTimeout(() => setFlash(null), 1600);
      }, 650);
    }, 1300);
  };
  const disconnect = (name: string) => {
    posthog.capture("integration_disconnected", { integration: name });
    setConnected((cs) => cs.filter((c) => c.name !== name));
  };

  return (
    <div style={{ maxWidth: 1120, margin: "0 auto" }} className="fade-up">
      <SectionHead title="Integrations" size="lg"
        sub="Connect the services BlinkBooks reads from and writes to. Your ledger, banks and inbox in one place."
        actions={<>
          <button className="btn btn-sec" onClick={() => { posthog.capture("integrations_date_range_clicked"); notify("Date range filter"); }}><Icon name="calendar" size={15} stroke="var(--ink-3)" /> Last 30 days</button>
          <button className="btn btn-sec" onClick={() => { posthog.capture("integrations_export_clicked"); notify("Export"); }}><Icon name="download" size={15} stroke="var(--ink-3)" /> Export</button>
        </>} />

      {/* Connected */}
      <div className="eyebrow" style={{ marginBottom: 10 }}>Connected · {connected.length}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 28 }}>
        {connected.map((c) => {
          const busy = syncingName === c.name;
          return (
            <div key={c.name} className="card" style={{
              padding: 18, display: "flex", alignItems: "center", gap: 16,
              borderColor: flash === c.name ? "var(--pos)" : "var(--line)",
              boxShadow: flash === c.name ? "0 0 0 3px var(--pos-soft)" : "none",
              transition: "border-color .3s,box-shadow .3s",
            }}>
              <LogoTile label={c.name} bg={c.bg} fg={c.fg} glyph={c.glyph} size={52} radius={13} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-.01em" }}>{c.name}</span>
                  <span className="badge b-pos"><span className="dot" style={{ background: "var(--pos)" }} />Connected</span>
                </div>
                <div style={{ fontSize: 12.5, color: "var(--ink-4)", marginTop: 3 }}>{c.sub}</div>
                <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 5 }}>Last sync <span style={{ color: "var(--ink-4)" }}>({busy ? "syncing…" : c.lastSync})</span></div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button className="btn btn-sec" onClick={() => doSync(c.name)} disabled={busy}>
                  <Icon name="refresh" size={15} stroke="var(--ink-3)" style={busy ? { animation: "bbSpin 1s linear infinite" } : undefined} /> {busy ? "Syncing…" : "Sync now"}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => disconnect(c.name)}>Disconnect</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Available */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 16 }}>
        <span className="eyebrow">Available</span>
      </div>
      {DATA.integrationsAvailable.map((sec) => {
        const items = sec.items.filter((it) => !isConnected(it.name));
        if (!items.length) return null;
        return (
          <div key={sec.group} style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ink-3)", marginBottom: 11 }}>{sec.group}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 }}>
              {items.map((it) => {
                const soon = it.note === "Coming soon";
                return (
                  <div key={it.name} className="card" style={{ padding: 18, opacity: soon ? 0.72 : 1, transition: "box-shadow .15s,border-color .15s" }}
                    onMouseEnter={(e) => { if (!soon) { e.currentTarget.style.boxShadow = "var(--sh-md)"; e.currentTarget.style.borderColor = "#C9DBE0"; } }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "var(--line)"; }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                      <LogoTile label={it.name} bg={it.bg} fg={(it as IntegrationItem).fg} glyph={it.glyph} soon={soon} radius={12} />
                      {soon
                        ? <span className="badge b-mute">Soon</span>
                        : <button className="btn btn-sec btn-sm" onClick={() => openConnect(it)}>Connect</button>}
                    </div>
                    <div style={{ fontSize: 14.5, fontWeight: 600, marginTop: 14 }}>{it.name}</div>
                    <div style={{ fontSize: 12, color: "var(--ink-4)", marginTop: 4, lineHeight: 1.5, minHeight: 34 }}>{it.note}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {connecting && <ConnectModal item={connecting} phase={phase} onConfirm={confirmConnect} onClose={() => setConnecting(null)} />}
    </div>
  );
};

/* Connect consent modal — OAuth-style flow */
const ConnectModal = ({ item, phase, onConfirm, onClose }: {
  item: IntegrationItem; phase: "consent" | "authorizing" | "done"; onConfirm: () => void; onClose: () => void;
}) => (
  <>
    <div onClick={phase === "authorizing" ? undefined : onClose} className="fade-up" style={{ position: "fixed", inset: 0, background: "rgba(23, 49, 58,.4)", zIndex: 80 }} />
    <div className="fade-up" style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 400, maxWidth: "92vw", background: "var(--surface)", borderRadius: 16, boxShadow: "var(--sh-lg)", zIndex: 81, overflow: "hidden" }}>
      <div style={{ padding: "24px 24px 0", display: "flex", alignItems: "center", justifyContent: "center", gap: 14 }}>
        <LogoTile label="B" bg="linear-gradient(140deg,#0A6D81,#075869)" glyph="✦" size={46} radius={12} />
        <Icon name={phase === "done" ? "check" : "more"} size={18} stroke="var(--ink-4)" />
        <LogoTile label={item.name} bg={item.bg} fg={item.fg} glyph={item.glyph} size={46} radius={12} />
      </div>
      <div style={{ padding: "18px 26px 24px", textAlign: "center" }}>
        {phase === "done" ? <>
          <div style={{ width: 44, height: 44, borderRadius: 50, background: "var(--pos-soft)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <Icon name="check" size={24} stroke="var(--pos)" />
          </div>
          <div style={{ fontSize: 16.5, fontWeight: 600 }}>{item.name} connected</div>
          <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 5 }}>Importing your data now — this can take a minute.</div>
        </> : <>
          <div style={{ fontSize: 16.5, fontWeight: 600, letterSpacing: "-.01em" }}>Connect {item.name}</div>
          <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 6, lineHeight: 1.55 }}>
            BlinkBooks will be able to <b style={{ color: "var(--ink-2)" }}>
              {item.name.match(/Gmail|Outlook/) ? "read receipts and invoices from" : item.name.match(/Drive|OneDrive/) ? "read and store files in" : "read financial data from"}
            </b> {item.name}. You can disconnect anytime.
          </div>
          <div style={{ display: "flex", gap: 9, marginTop: 20 }}>
            <button className="btn btn-sec" style={{ flex: 1 }} onClick={() => { posthog.capture("integration_connect_cancelled", { integration: item.name }); onClose(); }} disabled={phase === "authorizing"}>Cancel</button>
            <button className="btn btn-pri" style={{ flex: 1 }} onClick={onConfirm} disabled={phase === "authorizing"}>
              {phase === "authorizing" ? <><Icon name="refresh" size={15} stroke="#fff" style={{ animation: "bbSpin 1s linear infinite" }} /> Authorizing…</> : "Authorize"}
            </button>
          </div>
        </>}
      </div>
    </div>
  </>
);

/* ── Settings shell: left sub-nav + content ─────────────────────── */
const SETTINGS_NAV = [
  { id: "general", label: "General" },
  { id: "company", label: "Company" },
  { id: "integrations", label: "Integrations" },
  { id: "team", label: "Team" },
  { id: "billing", label: "Billing" },
  { id: "security", label: "Security" },
  { id: "developer", label: "Developer" },
];

export const SettingsHub = ({ initSection }: { initSection?: string }) => {
  const [sec, setSec] = useState(initSection || "general");
  const NavItem = ({ id, label }: { id: string; label: string }) => {
    const on = sec === id;
    return (
      <button onClick={() => { posthog.capture("settings_section_opened", { section: id }); setSec(id); }} style={{
        display: "block", width: "100%", textAlign: "left", border: "none", cursor: "pointer", font: "inherit",
        fontSize: 13.5, fontWeight: on ? 600 : 500, color: on ? "var(--brand-ink)" : "var(--ink-2)",
        background: on ? "var(--brand-tint)" : "transparent", padding: "8px 12px", borderRadius: 8,
        marginBottom: 2, transition: "background .12s",
      }}>{label}</button>
    );
  };
  const content = ({
    general: <GeneralSection />,
    company: <CompanySection />,
    integrations: <IntegrationsPage />,
    team: <TeamSection />,
    billing: <BillingSection />,
    security: <SimpleSection title="Security" sub="SSO, 2FA, audit log and sessions." />,
    developer: <SimpleSection title="Developer" sub="API keys, webhooks and logs." />,
  } as Record<string, React.ReactNode>)[sec];
  return (
    <div style={{ display: "flex", gap: 28, maxWidth: 1160, margin: "0 auto", alignItems: "flex-start" }} className="fade-up">
      <div style={{ width: 208, flexShrink: 0, position: "sticky", top: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: "-.01em", padding: "0 12px 12px" }}>Settings</div>
        {SETTINGS_NAV.map((n) => <NavItem key={n.id} id={n.id} label={n.label} />)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>{content}</div>
    </div>
  );
};
